import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAllStudentAssets,
  getStudentPresets,
  getUserAvatarConfig,
  saveUserAvatarConfig,
  updateUserAvatarConfig,
  buildAvatarLayers,
  DEFAULT_AVATAR_CONFIG,
  awardAvatarFirstSaveXP,
  getUserUnlockData,
  isAssetUnlocked,
  getUnlockDescription,
  getAssetOwnershipPercentages,
} from '../../services/avatarBuilderService';
import { getRules, evaluateCompatibility } from '../../services/avatarRulesService';
import { getCategories } from '../../services/avatarAssetService';

// ─── RARITY CONFIG ────────────────────────────────────────────────────────────

const RARITY_CONFIG = {
  common:    { label: 'Common',    color: 'text-gray-400',   bg: 'bg-gray-500/10',   border: 'border-gray-500/20'   },
  rare:      { label: 'Rare',      color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/20'   },
  epic:      { label: 'Epic',      color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  legendary: { label: 'Legendary', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
};

// ─── CATEGORY CONFIG ──────────────────────────────────────────────────────────

const CATEGORY_TABS = [
  { slug: 'backgrounds', label: 'Background', icon: 'Image', configKey: 'background' },
  { slug: 'bodies', label: 'Body', icon: 'User', configKey: 'bodyType' },
  { slug: 'eyes', label: 'Eyes', icon: 'Eye', configKey: 'eyes' },
  { slug: 'eyebrows', label: 'Brows', icon: 'Minus', configKey: 'eyebrows' },
  { slug: 'mouths', label: 'Mouth', icon: 'Smile', configKey: 'mouth' },
  { slug: 'hair_styles', label: 'Hair', icon: 'Wind', configKey: 'hairStyle' },
  { slug: 'facial_hair', label: 'Beard', icon: 'Feather', configKey: 'facialHair' },
  { slug: 'clothing', label: 'Outfit', icon: 'Shirt', configKey: 'clothing' },
  { slug: 'accessories', label: 'Extras', icon: 'Star', configKey: 'accessory' },
  { slug: 'hats', label: 'Hats', icon: 'Crown', configKey: 'hat' },
  { slug: 'fantasy', label: 'Fantasy', icon: 'Sparkles', configKey: 'wings' },
];

// ─── LAYERED AVATAR PREVIEW ───────────────────────────────────────────────────

const AvatarPreview = ({ layers, isEmpty }) => (
  <div className="relative w-full aspect-square max-w-xs mx-auto rounded-2xl overflow-hidden bg-gradient-to-b from-amber-950/30 to-stone-900/50 border-2 border-amber-700/30 shadow-2xl">
    {isEmpty ? (
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
        <div className="w-20 h-20 rounded-full bg-amber-700/20 flex items-center justify-center mb-3">
          <Icon name="User" size={40} className="text-amber-600/50" />
        </div>
        <p className="text-amber-600/60 text-sm">Choose assets below to build your character</p>
      </div>
    ) : (
      layers?.map((layer, i) => (
        <img
          key={`${layer?.slug}-${i}`}
          src={layer?.publicUrl}
          alt={layer?.slug}
          className="absolute inset-0 w-full h-full object-contain"
          style={{ zIndex: layer?.layer_order }}
        />
      ))
    )}
    {/* Theatrical frame overlay */}
    <div className="absolute inset-0 pointer-events-none rounded-2xl ring-1 ring-amber-600/20"></div>
  </div>
);

// ─── ASSET OPTION CARD ────────────────────────────────────────────────────────

const AssetCard = ({ asset, isSelected, isDisabled, isLocked, lockDescription, onSelect, ownershipPct }) => {
  const rarity = asset?.rarity || 'common';
  const rarityCfg = RARITY_CONFIG?.[rarity] || RARITY_CONFIG?.common;
  const isLegendary = rarity === 'legendary';

  return (
    <button
      onClick={() => !isDisabled && !isLocked && onSelect(asset)}
      disabled={isDisabled || isLocked}
      title={isLocked ? lockDescription : undefined}
      className={`relative group rounded-xl overflow-hidden border-2 transition-all duration-200 text-left w-full min-h-[80px] sm:min-h-0 ${
        isSelected
          ? 'border-amber-500 ring-2 ring-amber-500/40 shadow-lg shadow-amber-500/20'
          : isLocked
          ? 'border-amber-700/40 opacity-70 cursor-not-allowed'
          : isDisabled
          ? 'border-border opacity-40 cursor-not-allowed'
          : `${rarityCfg?.border} hover:border-amber-500/50 hover:shadow-md cursor-pointer`
      }`}>
      <div className="aspect-square bg-stone-800/50 relative">
        {/* For hair assets: render hair_back behind hair_front */}
        {asset?.hair_back_storage_path && asset?.publicUrl ? (
          <>
            <img
              src={asset?.hair_back_public_url || asset?.publicUrl}
              alt={`${asset?.display_name || asset?.slug} back`}
              className="absolute inset-0 w-full h-full object-contain p-1"
              style={{ zIndex: 1 }}
              onError={e => { e.currentTarget.style.display = 'none'; }}
            />
            <img
              src={asset?.publicUrl}
              alt={asset?.display_name || asset?.style_variant || asset?.slug}
              className="absolute inset-0 w-full h-full object-contain p-1"
              style={{ zIndex: 2 }}
            />
          </>
        ) : asset?.avatar_asset_categories?.slug === 'bodies' || asset?.body_head_storage_path ? (
          <>
            {asset?.publicUrl && (
              <img
                src={asset?.publicUrl}
                alt={`${asset?.display_name || asset?.slug} main`}
                className="absolute inset-0 w-full h-full object-contain p-1"
                style={{ zIndex: 1 }}
              />
            )}
            {asset?.body_head_storage_path && asset?.body_head_public_url && (
              <img
                src={asset?.body_head_public_url}
                alt={`${asset?.display_name || asset?.slug} head`}
                className="absolute inset-0 w-full h-full object-contain p-1"
                style={{ zIndex: 2 }}
                onError={e => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            {!asset?.publicUrl && (
              <div className="w-full h-full flex items-center justify-center">
                <Icon name="Image" size={24} className="text-muted-foreground/30" />
              </div>
            )}
          </>
        ) : asset?.publicUrl ? (
          <img src={asset?.publicUrl} alt={asset?.display_name || asset?.style_variant || asset?.slug} className="w-full h-full object-contain p-1" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name="Image" size={24} className="text-muted-foreground/30" />
          </div>
        )}
        {isSelected && (
          <div className="absolute top-1 right-1 w-5 h-5 sm:w-5 sm:h-5 bg-amber-500 rounded-full flex items-center justify-center" style={{ zIndex: 10 }}>
            <Icon name="Check" size={12} color="white" />
          </div>
        )}
        {isDisabled && !isLocked && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40" style={{ zIndex: 10 }}>
            <Icon name="Ban" size={20} className="text-red-400/70" />
          </div>
        )}
        {isLocked && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/55 backdrop-blur-[1px]" style={{ zIndex: 10 }}>
            <Icon name="Lock" size={18} className="text-amber-400/90" />
            {/* Show rarity indicator on locked assets */}
            <span className={`mt-1 px-1.5 py-0.5 rounded text-xs font-semibold ${rarityCfg?.bg} ${rarityCfg?.color} border ${rarityCfg?.border}`}>
              {isLegendary ? '✦ ' : ''}{rarityCfg?.label}
            </span>
          </div>
        )}
        {/* Rarity corner indicator for unlocked assets */}
        {!isLocked && rarity !== 'common' && (
          <div className="absolute top-1 left-1 z-10">
            <span className={`px-1 py-0.5 rounded text-xs font-bold ${rarityCfg?.bg} ${rarityCfg?.color} border ${rarityCfg?.border} leading-none`}>
              {isLegendary ? '✦' : rarity === 'epic' ? '◆' : '●'}
            </span>
          </div>
        )}
      </div>
      <div className="p-1.5 sm:p-1.5">
        <p className="text-xs text-foreground truncate leading-tight">
          {asset?.display_name || asset?.style_variant || asset?.slug?.replace(/_/g, ' ')}
        </p>
        {/* Rarity label */}
        <span className={`text-xs font-semibold ${rarityCfg?.color}`}>
          {isLegendary ? '✦ ' : ''}{rarityCfg?.label}
        </span>
        {/* Ownership percentage */}
        {ownershipPct !== undefined && (
          <p className="text-xs text-muted-foreground/70 leading-tight mt-0.5">
            {ownershipPct}% own this
          </p>
        )}
        {isLocked && lockDescription && (
          <p className="text-xs text-amber-500/80 truncate leading-tight mt-0.5">{lockDescription}</p>
        )}
      </div>
    </button>
  );
};

// ─── PRESET CARD ──────────────────────────────────────────────────────────────

const PresetCard = ({ preset, onApply }) => (
  <button
    onClick={() => onApply(preset)}
    className="bg-card border border-border rounded-xl p-4 text-left hover:border-amber-500/50 hover:shadow-md transition-all duration-200 group min-h-[44px]">
    <div className="w-12 h-12 bg-gradient-to-br from-amber-500/20 to-purple-500/20 rounded-xl flex items-center justify-center mb-3 group-hover:from-amber-500/30 group-hover:to-purple-500/30 transition-all">
      <Icon name="Sparkles" size={24} className="text-amber-400" />
    </div>
    <h4 className="font-semibold text-foreground text-sm mb-1">{preset?.name}</h4>
    {preset?.description && <p className="text-xs text-muted-foreground line-clamp-2">{preset?.description}</p>}
  </button>
);

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function StudentAvatarStudio() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();

  const [categories, setCategories] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [rules, setRules] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedConfigId, setSavedConfigId] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showXPBadge, setShowXPBadge] = useState(false);
  const [unlockData, setUnlockData] = useState({ xp: 0, badges: [], achievementIds: new Set() });
  const [ownershipMap, setOwnershipMap] = useState({});

  const saveButtonRef = useRef(null);

  const [avatarConfig, setAvatarConfig] = useState({ ...DEFAULT_AVATAR_CONFIG });
  const [activeTab, setActiveTab] = useState('backgrounds');
  const [showPresets, setShowPresets] = useState(false);

  // Load all data
  useEffect(() => {
    document.title = 'Avatar Studio | BardPlay';
    const loadData = async () => {
      setLoading(true);
      try {
        const [cats, assets, rulesData, presetsData, unlockDataResult, ownershipData] = await Promise.all([
          getCategories(),
          getAllStudentAssets(),
          getRules(true),
          getStudentPresets(),
          user?.id ? getUserUnlockData(user?.id) : Promise.resolve({ xp: 0, badges: [], achievementIds: new Set() }),
          getAssetOwnershipPercentages(),
        ]);
        setCategories(cats);
        setAllAssets(assets);
        setRules(rulesData);
        setPresets(presetsData);
        setUnlockData(unlockDataResult);
        setOwnershipMap(ownershipData || {});

        // Load existing avatar config
        if (user?.id) {
          const existing = await getUserAvatarConfig(user?.id);
          if (existing?.avatar_config) {
            setAvatarConfig({ ...DEFAULT_AVATAR_CONFIG, ...existing?.avatar_config });
            setSavedConfigId(existing?.id);
          }
        }
      } catch (err) {
        console.error('Error loading avatar studio:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user?.id]);

  // Group assets by category slug
  const assetsByCategorySlug = useMemo(() => {
    const map = {};
    for (const asset of allAssets) {
      const slug = asset?.avatar_asset_categories?.slug;
      if (slug) {
        if (!map?.[slug]) map[slug] = [];
        map?.[slug]?.push(asset);
      }
    }
    return map;
  }, [allAssets]);

  // Build layers for preview
  const layers = useMemo(() => buildAvatarLayers(avatarConfig, assetsByCategorySlug), [avatarConfig, assetsByCategorySlug]);

  // Get selected slugs for compatibility check
  const selectedSlugs = useMemo(() => Object.values(avatarConfig)?.filter(Boolean), [avatarConfig]);

  // Evaluate compatibility
  const { disabledCategories, disabledSlugs } = useMemo(
    () => evaluateCompatibility(rules, selectedSlugs, avatarConfig),
    [rules, selectedSlugs, avatarConfig]
  );

  // Current tab assets
  const currentTabConfig = CATEGORY_TABS?.find(t => t?.slug === activeTab);
  const currentAssets = assetsByCategorySlug?.[activeTab] || [];

  const handleSelectAsset = useCallback((asset) => {
    const tabConfig = CATEGORY_TABS?.find(t => t?.slug === activeTab);
    if (!tabConfig) return;
    setAvatarConfig(prev => {
      const currentValue = prev?.[tabConfig?.configKey];
      // Toggle off if already selected
      if (currentValue === asset?.slug || currentValue === asset?.style_variant) {
        return { ...prev, [tabConfig?.configKey]: null };
      }
      return { ...prev, [tabConfig?.configKey]: asset?.slug };
    });
  }, [activeTab]);

  const handleApplyPreset = useCallback((preset) => {
    setAvatarConfig({ ...DEFAULT_AVATAR_CONFIG, ...preset?.avatar_config });
    setShowPresets(false);
  }, []);

  const handleRandomize = useCallback(() => {
    const newConfig = { ...DEFAULT_AVATAR_CONFIG };
    for (const tab of CATEGORY_TABS) {
      const assets = (assetsByCategorySlug?.[tab?.slug] || [])?.filter(
        a => a?.is_active !== false && a?.is_archived !== true && a?.is_student_visible !== false
          && isAssetUnlocked(a, unlockData)
      );
      if (assets?.length > 0) {
        const random = assets?.[Math.floor(Math.random() * assets?.length)];
        newConfig[tab.configKey] = random?.slug;
      }
    }
    setAvatarConfig(newConfig);
  }, [assetsByCategorySlug, unlockData]);

  const handleReset = useCallback(() => {
    setAvatarConfig({ ...DEFAULT_AVATAR_CONFIG });
  }, []);

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    const isFirstSave = !savedConfigId;
    try {
      let saved;
      if (savedConfigId) {
        // Update the existing active config row in-place
        saved = await updateUserAvatarConfig(savedConfigId, avatarConfig);
      } else {
        // First save — insert a new active config
        saved = await saveUserAvatarConfig(user?.id, avatarConfig);
        setSavedConfigId(saved?.id);
      }
      if (isFirstSave) {
        await awardAvatarFirstSaveXP(user?.id);
        // 🎉 First-save celebration
        triggerFirstSaveCelebration();
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving avatar:', err);
    } finally {
      setSaving(false);
    }
  };

  const triggerFirstSaveCelebration = useCallback(async () => {
    // Confetti burst with theatre colors
    try {
      const confetti = (await import('canvas-confetti'))?.default;
      const theatreColors = ['#F5C842', '#7B1C3E', '#5B2D8E', '#F5ECD7', '#C0392B', '#8E44AD'];
      // First burst — center
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { x: 0.5, y: 0.55 },
        colors: theatreColors,
        startVelocity: 45,
        gravity: 0.9,
        ticks: 200,
      });
      // Side bursts after short delay
      setTimeout(() => {
        confetti({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0, y: 0.65 }, colors: theatreColors });
        confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1, y: 0.65 }, colors: theatreColors });
      }, 200);
    } catch (e) {
      console.warn('Confetti unavailable:', e);
    }
    // Animated +50 XP badge
    setShowXPBadge(true);
    setTimeout(() => setShowXPBadge(false), 2200);
  }, []);

  const isAvatarEmpty = layers?.length === 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-purple-500 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Icon name="Wand2" size={32} color="white" />
          </div>
          <p className="text-muted-foreground">Loading your Avatar Studio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation user={user} isAuthenticated={true} notifications={[]} />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">

          {/* Header */}
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-500 to-purple-600 flex items-center justify-center shadow-lg flex-shrink-0">
                <Icon name="Wand2" size={20} color="white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-foreground">Avatar Studio</h1>
                <p className="text-xs sm:text-sm text-muted-foreground">Build your Shakespearean character</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPresets(p => !p)}
                className="flex items-center gap-1.5 px-3 py-2.5 sm:px-4 sm:py-2.5 min-h-[44px] bg-card border border-border hover:border-amber-500/50 text-foreground rounded-xl text-sm font-medium transition-all">
                <Icon name="Sparkles" size={16} className="text-amber-400" />
                <span className="hidden sm:inline">Presets</span>
              </button>
              <button
                onClick={() => navigate('/student-stage-dashboard')}
                className="flex items-center gap-1.5 px-3 py-2.5 sm:px-4 sm:py-2.5 min-h-[44px] bg-card border border-border hover:border-amber-500/50 text-foreground rounded-xl text-sm font-medium transition-all">
                <Icon name="ArrowLeft" size={16} />
                <span className="hidden sm:inline">Dashboard</span>
              </button>
            </div>
          </div>

          {/* Main layout: stacks vertically on mobile, side-by-side on lg+ */}
          <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 sm:gap-6">

            {/* TOP on mobile / LEFT on desktop: Avatar Preview + Controls */}
            <div className="lg:col-span-1 space-y-4">
              {/* Preview */}
              <div className="bg-card border border-border rounded-2xl p-4 sm:p-6">
                {/* On mobile, show preview at a constrained size; on desktop full width */}
                <div className="max-w-[200px] sm:max-w-xs mx-auto">
                  <AvatarPreview layers={layers} isEmpty={isAvatarEmpty} />
                </div>

                {/* Action Buttons — larger touch targets on mobile */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={handleRandomize}
                    className="flex-1 flex items-center justify-center gap-2 py-3 sm:py-2.5 min-h-[48px] sm:min-h-[44px] bg-amber-500/10 hover:bg-amber-500/20 active:bg-amber-500/30 border border-amber-500/30 text-amber-400 rounded-xl text-sm font-medium transition-all">
                    <Icon name="Shuffle" size={16} />
                    Randomize
                  </button>
                  <button
                    onClick={handleReset}
                    className="flex-1 flex items-center justify-center gap-2 py-3 sm:py-2.5 min-h-[48px] sm:min-h-[44px] bg-muted/50 hover:bg-muted active:bg-muted/80 border border-border text-muted-foreground hover:text-foreground rounded-xl text-sm font-medium transition-all">
                    <Icon name="RotateCcw" size={16} />
                    Reset
                  </button>
                </div>

                {/* Save Button — large touch target */}
                <div className="relative mt-3">
                  <button
                    ref={saveButtonRef}
                    onClick={handleSave}
                    disabled={saving || isAvatarEmpty}
                    className={`w-full py-3.5 sm:py-3 min-h-[52px] sm:min-h-[48px] rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                      saveSuccess
                        ? 'bg-green-500 text-white'
                        : isAvatarEmpty
                        ? 'bg-muted text-muted-foreground cursor-not-allowed'
                        : 'bg-gradient-to-r from-amber-500 to-purple-600 hover:from-amber-400 hover:to-purple-500 active:from-amber-600 active:to-purple-700 text-white shadow-lg hover:shadow-amber-500/25'
                    }`}>
                    {saving ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Saving...</>
                    ) : saveSuccess ? (
                      <><Icon name="CheckCircle" size={16} /> Avatar Saved!</>
                    ) : (
                      <><Icon name="Save" size={16} /> Save Avatar</>
                    )}
                  </button>
                  {/* Animated +50 XP badge */}
                  {showXPBadge && (
                    <div
                      className="absolute left-1/2 pointer-events-none"
                      style={{
                        bottom: '100%',
                        transform: 'translateX(-50%)',
                        animation: 'xpBadgeFly 2.2s ease-out forwards',
                      }}>
                      <div className="bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 font-extrabold text-lg px-5 py-2 rounded-full shadow-2xl shadow-amber-500/50 whitespace-nowrap flex items-center gap-1.5 border-2 border-amber-200">
                        <span>⭐</span>
                        <span>+50 XP</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Current Selections Summary — hidden on mobile to save space, visible on lg */}
              <div className="hidden lg:block bg-card border border-border rounded-2xl p-4">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="List" size={14} />
                  Current Selections
                </h3>
                <div className="space-y-1.5">
                  {CATEGORY_TABS?.map(tab => {
                    const value = avatarConfig?.[tab?.configKey];
                    const asset = value ? allAssets?.find(a => a?.slug === value) : null;
                    const displayLabel = asset?.display_name || (value ? value?.replace(/_/g, ' ') : null);
                    return (
                      <div key={tab?.slug} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{tab?.label}</span>
                        <span className={`font-medium truncate max-w-[120px] text-right ${value ? 'text-amber-400' : 'text-muted-foreground/40'}`}>
                          {displayLabel || '—'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* BOTTOM on mobile / RIGHT on desktop: Category Tabs + Asset Grid */}
            <div className="lg:col-span-2 space-y-4">

              {/* Presets Panel */}
              {showPresets && (
                <div className="bg-card border border-amber-500/30 rounded-2xl p-4 sm:p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-foreground flex items-center gap-2">
                      <Icon name="Sparkles" size={16} className="text-amber-400" />
                      Themed Presets
                    </h3>
                    <button
                      onClick={() => setShowPresets(false)}
                      className="text-muted-foreground hover:text-foreground p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-muted/50 transition-all">
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                  {presets?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No presets available yet.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {presets?.map(preset => (
                        <PresetCard key={preset?.id} preset={preset} onApply={handleApplyPreset} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Category Tabs — horizontally scrollable with larger tap areas on mobile */}
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div
                  className="flex overflow-x-auto border-b border-border bg-muted/20 scrollbar-hide"
                  style={{ WebkitOverflowScrolling: 'touch' }}>
                  {CATEGORY_TABS?.map(tab => {
                    const isDisabledCat = disabledCategories?.has(tab?.slug);
                    const hasSelection = !!avatarConfig?.[tab?.configKey];
                    return (
                      <button
                        key={tab?.slug}
                        onClick={() => setActiveTab(tab?.slug)}
                        className={`flex flex-col items-center gap-1 px-4 sm:px-4 py-3.5 sm:py-3 min-w-[64px] sm:min-w-0 min-h-[60px] sm:min-h-[52px] text-xs font-medium whitespace-nowrap transition-all duration-200 border-b-2 relative flex-shrink-0 ${
                          activeTab === tab?.slug
                            ? 'border-amber-500 text-amber-400 bg-amber-500/5'
                            : isDisabledCat
                            ? 'border-transparent text-muted-foreground/40'
                            : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30 active:bg-muted/50'
                        }`}>
                        <Icon name={tab?.icon} size={18} />
                        {tab?.label}
                        {hasSelection && !isDisabledCat && (
                          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                        )}
                        {isDisabledCat && (
                          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Asset Grid — 2 cols on mobile, 3 on sm, 4-5 on larger */}
                <div className="p-3 sm:p-4">
                  {disabledCategories?.has(activeTab) && (
                    <div className="mb-4 flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-400">
                      <Icon name="AlertTriangle" size={16} />
                      This category is disabled by your current selections.
                    </div>
                  )}

                  {currentAssets?.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Icon name="ImageOff" size={40} className="mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No assets available in this category yet.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
                      {currentAssets?.map(asset => {
                        const tabConfig = CATEGORY_TABS?.find(t => t?.slug === activeTab);
                        const currentValue = tabConfig ? avatarConfig?.[tabConfig?.configKey] : null;
                        const isSelected = currentValue === asset?.slug;
                        const isDisabled = disabledSlugs?.has(asset?.slug) || disabledCategories?.has(activeTab);
                        const isLocked = !isAssetUnlocked(asset, unlockData);
                        const lockDescription = isLocked ? getUnlockDescription(asset) : null;
                        const ownershipPct = ownershipMap?.[asset?.slug];
                        return (
                          <AssetCard
                            key={asset?.id}
                            asset={asset}
                            isSelected={isSelected}
                            isDisabled={isDisabled}
                            isLocked={isLocked}
                            lockDescription={lockDescription}
                            onSelect={handleSelectAsset}
                            ownershipPct={ownershipPct}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Compatibility Notice */}
              {(disabledCategories?.size > 0 || disabledSlugs?.size > 0) && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-start gap-2">
                  <Icon name="Info" size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-300">
                    Some options are disabled due to compatibility rules. Your current selections affect what can be combined.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
